class HeaderDrawer extends HTMLElement {
  constructor() {
    super();
    this.dialogManager = null;
  }

  connectedCallback() {
    // Ensure DOM is ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.waitForDialogManager());
    } else {
      this.waitForDialogManager();
    }
  }

  waitForDialogManager() {
    // Wait for DialogManager to be available
    if (window.DialogManager) {
      this.init();
    } else {
      // If DialogManager isn't loaded yet, wait for it
      const checkInterval = setInterval(() => {
        if (window.DialogManager) {
          clearInterval(checkInterval);
          this.init();
        }
      }, 50);
    }
  }

  init() {
    const menuButton = document.querySelector('#header-menu-button');
    const overlay = document.getElementById('HeaderDrawer-Overlay');
    const dialogElement = this.querySelector('#HeaderDrawer-Dialog');
    const closeButton = this.querySelector('#HeaderDrawer-Close');

    if (!menuButton || !dialogElement) {
      console.error('HeaderDrawer: Missing required elements', {
        menuButton: !!menuButton,
        dialogElement: !!dialogElement,
      });
      return;
    }

    // Create DialogManager instance - pass dialogElement, not this
    this.dialogManager = new DialogManager(dialogElement, {
      type: 'drawer-left',
      triggerElement: menuButton,
      overlayElement: overlay,
      closeElements: closeButton ? [closeButton] : [],
      onOpen: () => {
        this.setAttribute('aria-hidden', 'false');
        // Ensure the translate class is properly removed
        dialogElement.classList.remove('-translate-x-full');
        dialogElement.classList.add('shadow-xl');
        // Close cart drawer if it's open
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.dialogManager) {
          cartDrawer.dialogManager.close();
        }
        // Update active filter states when drawer opens
        setTimeout(() => {
          updateHeaderDrawerActiveFilterStates();
        }, 50);
      },
      onClose: () => {
        this.setAttribute('aria-hidden', 'true');
        // Ensure the translate class is properly added back
        dialogElement.classList.add('-translate-x-full');
        dialogElement.classList.remove('shadow-xl');
      },
    });

    // Set aria-label for accessibility
    this.setAttribute('aria-label', 'Menu');

    // Expose methods for external use
    this.enhancedOpen = () => this.dialogManager?.open();
    this.enhancedClose = () => this.dialogManager?.close();
  }
}

// Define custom element
if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

// Check for active filter states based on current URL
function updateHeaderDrawerActiveFilterStates() {
  const urlParams = new URLSearchParams(window.location.search);
  const filterLinks = document.querySelectorAll('#HeaderDrawer-Dialog a[href*="filter.p.m.custom"]');

  filterLinks.forEach((link) => {
    // Extract filter parameter from href
    const href = link.getAttribute('href');
    let isActive = false;

    try {
      const url = new URL(href, window.location.origin);
      const linkParams = new URLSearchParams(url.search);

      // Check each parameter in the link's URL against current URL parameters
      for (const [paramName, paramValue] of linkParams.entries()) {
        if (paramName.startsWith('filter.p.m.custom')) {
          const currentValue = urlParams.get(paramName);

          // Try multiple comparison methods for robust matching
          if (currentValue === paramValue) {
            isActive = true;
            break;
          }

          // Try decoded comparison
          try {
            const decodedParamValue = decodeURIComponent(paramValue);
            const decodedCurrentValue = currentValue ? decodeURIComponent(currentValue) : null;
            if (decodedCurrentValue === decodedParamValue) {
              isActive = true;
              break;
            }
          } catch (e) {
            // Ignore decode errors
          }
        }
      }
    } catch (e) {
      console.warn('Error parsing filter link URL:', href, e);
    }

    // Update the link's active state
    if (isActive) {
      link.classList.add('item-active');
      link.setAttribute('aria-current', 'page');
    } else {
      link.classList.remove('item-active');
      link.removeAttribute('aria-current');
    }
  });
}

// Add click event listeners to filter links for immediate active state updates
function addFilterClickListeners() {
  const filterLinks = document.querySelectorAll('#HeaderDrawer-Dialog a[href*="filter.p.m.custom"]');

  filterLinks.forEach((link) => {
    link.addEventListener('click', function (e) {
      // Small delay to allow URL to update before checking active states
      setTimeout(() => {
        updateHeaderDrawerActiveFilterStates();
      }, 10);
    });
  });
}

// Run active state detection multiple times to ensure it catches the state
function runActiveStateDetection() {
  updateHeaderDrawerActiveFilterStates();
  addFilterClickListeners();

  // Run again after a short delay to catch any dynamic content
  setTimeout(updateHeaderDrawerActiveFilterStates, 100);
  setTimeout(updateHeaderDrawerActiveFilterStates, 500);
}

// Update active states when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runActiveStateDetection);
} else {
  runActiveStateDetection();
}

// Also run when the page is fully loaded
window.addEventListener('load', updateHeaderDrawerActiveFilterStates);

// Update active states when URL changes (for SPA-like behavior)
window.addEventListener('popstate', updateHeaderDrawerActiveFilterStates);
